<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('menu_listing'), link: '/menus' },
          { name: $t('product_listing'), link: `/menus/${$route.params.menuId}/products` },
          { name: $t($route.params.id === 'new' ? 'product_form_new' : 'product_form_edit') }
        ]" />
        <h1>{{ $t($route.params.id === 'new' ? 'product_form_new' : 'product_form_edit') }}</h1>
      </header>
      <article class="container framework-slot">
        <div v-if="product.loading || productCategories.loading">
            <loading-state/>
        </div>
        <div v-else-if="product.error || productCategories.error" class="alert alert-danger" role="alert">
            {{ $t('failed_retrieve_data_from_server') }} <button class="btn btn-link" @click="tryAgain">{{ $t('try_again') }}</button>
        </div>
        <form v-else v-on:submit.prevent="saveProduct">
            <div v-if="validationError" class="alert alert-danger" role="alert">
                {{validationError}}
            </div>
            <div v-if="product.node.isDeleted" class="alert alert-warning" role="alert">
                {{ $t('warning_record_is_deleted') }}
            </div>
            <form-page>
                <div class="form-row">
                    <form-group class="col-12 col-sm-6 required" :label="$t('name')" label-for="name" prepend-icon="tag"
                                :invalid-feedback="validationErrors.name">
                        <input id="name" autocomplete="name" type="text" class="form-control"
                               v-model="product.node.name" v-focus>
                    </form-group>
                    <form-group class="col-sm-3 required" :label="$t('product_category')" label-for="product-category-id" prepend-icon="layer-group"
                                :invalid-feedback="validationErrors.productCategoryId">
                        <select id="product-category-id" class="custom-select" v-model="product.node.productCategoryId">
                            <option v-for="productCategory in productCategories.nodes" :key="productCategory.id" :value="productCategory.id">{{productCategory.name}}</option>
                        </select>
                    </form-group>
                    <form-group class="col-sm-3" :label="$t('price')" label-for="price" prepend-icon="hashtag"
                                :invalid-feedback="validationErrors.price">
                        <money
                            id="price"
                            v-model="product.node.price"
                            v-bind="$getMoney()"
                            class="form-control"
                        />
                    </form-group>
                    <form-group class="col-12" :label="$t('photo')" label-for="photo-url"
                                :invalid-feedback="validationErrors.photoUrl"
                                :description="!product.node.photoUrl ? $t('photo_description') : undefined">
                        <input-image ref="photoInput" id="photo-url" v-model="product.node.photoUrl" :width="592" :height="333" :thumbs="[{width: 82, height: 82}]" @thumbsReady="[product.node.photoThumbUrl] = $event" />
                    </form-group>
                    <form-group class="col-12" :label="$t('description')" label-for="description" prepend-icon="quote-right"
                                :description="$t('description_details')"
                                :invalid-feedback="validationErrors.description">
                        <textarea id="description" class="form-control" rows="3" v-model="product.node.description"></textarea>
                    </form-group>
                </div>
                <div class="choices-container">
                    <div class="choices-header form-row">
                        <div class="col">
                            <label>{{$t('choices')}}</label>
                        </div>
                        <div v-if="product.node.choices && product.node.choices.length > 1" class="col-auto">
                            <button v-if="!product.reorderChoicesEnabled" type="button" class="btn btn-outline-secondary btn-sm" @click="product.reorderChoicesEnabled = true">{{$t('reorder_choices')}}</button>
                            <button v-if="product.reorderChoicesEnabled" type="button" class="btn btn-outline-primary btn-sm" v-on:click="product.reorderChoicesEnabled = false">{{ $t("reorder_choices_ok") }}</button>
                        </div>
                        <div class="col-auto">
                            <button v-if="!product.reorderChoicesEnabled" type="button" class="btn btn-secondary btn-sm" @click="addChoice">{{$t('choice_add')}}</button>
                        </div>
                    </div>
                    <div class="choices" :class="{'reorder-choices-enabled': product.reorderChoicesEnabled}">
                        <template v-if="product.node.choices && product.node.choices.length">
                            <draggable v-model="product.node.choices" :group="'product-choices'" :disabled="!product.reorderChoicesEnabled">
                                <div v-for="(choice, choiceIndex) in product.node.choices" :key="`choices-${choiceIndex}`" class="choice">
                                    <h3 v-if="product.reorderChoicesEnabled" v-text="choice.title || $t('choice_empty_title')" class="choice-reorder-title" />
                                    <template v-if="!product.reorderChoicesEnabled">
                                        <div class="form-row">
                                            <form-group class="form-group-sm col-12 col-sm-6 required" :label="$t('choice_title')" :label-for="`choices-${choiceIndex}-title`"
                                                        :invalid-feedback="validationErrors[`choices${choiceIndex}Title`]">
                                                <input :id="`choices-${choiceIndex}-title`" type="text" class="form-control form-control-sm" v-model="choice.title">
                                            </form-group>
                                            <form-group class="form-group-sm col-12 col-sm-6 required" :label="$t('choice_type')" :label-for="`choices-${choiceIndex}-type`"
                                                        :invalid-feedback="validationErrors[`choices${choiceIndex}Type`]">
                                                <select :id="`choices-${choiceIndex}-type`" class="custom-select custom-select-sm" v-model="choice.type">
                                                    <option v-for="choiceType in productChoiceTypes" :key="choiceType" :value="choiceType">{{$t("choice_type_" + choiceType.toLowerCase())}}</option>
                                                </select>
                                            </form-group>
                                        </div>
                                        <div v-if="['OPTIONS_MULTIPLE_CHOICE', 'OPTIONS_REPEATABLE_CHOICE'].indexOf(choice.type) !== -1" class="form-row">
                                          <form-group class="form-group-sm col-12 required" :label="$t('charge_only_most_expensive_option_label')"
                                                      :description="$t('charge_only_most_expensive_option_description')" :label-for="`choices-${choiceIndex}-charge-only-most-expensive-option`"
                                                      :invalid-feedback="validationErrors[`choices${choiceIndex}ChargeOnlyMostExpensiveOption`]">
                                            <select :id="`choices-${choiceIndex}-charge-only-most-expensive-option`" class="custom-select custom-select-sm" v-model="choice.chargeOnlyMostExpensiveOption">
                                              <option :value="false">{{$t('charge_only_most_expensive_option_no')}}</option>
                                              <option :value="true">{{$t('charge_only_most_expensive_option_yes')}}</option>
                                            </select>
                                          </form-group>
                                        </div>
                                        <div v-if="['OPTIONS_MULTIPLE_CHOICE', 'OPTIONS_REPEATABLE_CHOICE'].indexOf(choice.type) !== -1" class="form-row">
                                            <form-group class="form-group-sm col-6 required" :label="$t('choice_min_options')" :label-for="`choices-${choiceIndex}-min-options`"
                                                        :invalid-feedback="validationErrors[`choices${choiceIndex}MinOptions`]">
                                                <input :id="`choices-${choiceIndex}-min-options`" type="number" step="1" class="form-control form-control-sm"
                                                       v-model="choice.minOptions">
                                            </form-group>
                                            <form-group class="form-group-sm col-6 required" :label="$t('choice_max_options')" :label-for="`choices-${choiceIndex}-max-options`"
                                                        :invalid-feedback="validationErrors[`choices${choiceIndex}MaxOptions`]">
                                                <input :id="`choices-${choiceIndex}-max-options`" type="number" step="1" class="form-control form-control-sm"
                                                       v-model="choice.maxOptions">
                                            </form-group>
                                        </div>
                                        <div v-if="['OPTIONS_SINGLE_CHOICE', 'OPTIONS_MULTIPLE_CHOICE', 'OPTIONS_REPEATABLE_CHOICE'].indexOf(choice.type) !== -1">
                                            <div class="choice-options-header form-row">
                                                <div class="col">
                                                    <label class="choice-options-label">{{$t("choice_options")}}</label>
                                                </div>
                                                <div v-if="choice.options && choice.options.length > 1" class="col-auto">
                                                    <button v-if="!choice.reorderOptionsEnabled" type="button" class="btn btn-outline-secondary btn-sm" @click="choice.reorderOptionsEnabled = true">{{$t('reorder_choice_options')}}</button>
                                                    <button v-if="choice.reorderOptionsEnabled" type="button" class="btn btn-outline-primary btn-sm" v-on:click="choice.reorderOptionsEnabled = false">{{ $t("reorder_choice_options_ok") }}</button>
                                                </div>
                                                <div v-if="!choice.reorderOptionsEnabled" class="col-auto">
                                                    <button type="button" class="btn btn-secondary btn-sm" @click="addChoiceOption(choice)">{{$t('choice_option_add')}}</button>
                                                </div>
                                            </div>
                                            <div v-if="choice.options && choice.options.length" class="choice-options" :class="{'reorder-options-enabled': choice.reorderOptionsEnabled}">
                                                <draggable v-model="choice.options" :group="'product-choice-'+choiceIndex+'-options'" :disabled="!choice.reorderOptionsEnabled">
                                                    <div class="choice-option" v-for="(option, optionIndex) in choice.options" :key="`choices-${choiceIndex}-options-${optionIndex}`">
                                                        <h4 v-if="choice.reorderOptionsEnabled" v-text="option.description || $t('choice_option_empty_description')" class="choice-option-reorder-description" />
                                                        <template v-if="!choice.reorderOptionsEnabled">
                                                            <div class="form-row">
                                                                <form-group class="form-group-sm col-8 col-sm-9 required" :label="$t('choice_option_description')" :label-for="`choices-${choiceIndex}-options-${optionIndex}-description`"
                                                                            :invalid-feedback="validationErrors[`choices${choiceIndex}Options${optionIndex}Description`]">
                                                                    <input :id="`choices-${choiceIndex}-options-${optionIndex}-description`" type="text" class="form-control form-control-sm"
                                                                           v-model="option.description">
                                                                </form-group>
                                                                <form-group class="form-group-sm col-4 col-sm-3" :label="$t('choice_option_price')" :label-for="`choices-${choiceIndex}-options-${optionIndex}-price`"
                                                                            :invalid-feedback="validationErrors[`choices${choiceIndex}Options${optionIndex}Price`]">
                                                                    <money
                                                                        :id="`choices-${choiceIndex}-options-${optionIndex}-price`"
                                                                        v-model="option.price"
                                                                        v-bind="$getMoney()"
                                                                        class="form-control form-control-sm"
                                                                    />
                                                                </form-group>
                                                                <form-group class="form-group-sm col-12" :label="$t('choice_option_details')" :label-for="`choices-${choiceIndex}-options-${optionIndex}-details`"
                                                                            :invalid-feedback="validationErrors[`choices${choiceIndex}Options${optionIndex}Details`]">
                                                                    <input :id="`choices-${choiceIndex}-options-${optionIndex}-details`" type="text" class="form-control form-control-sm"
                                                                           v-model="option.details">
                                                                </form-group>
                                                            </div>
                                                            <div class="form-row">
                                                                <div class="col">
                                                                    <button type="button" class="btn btn-sm btn-outline-danger" @click="removeChoiceOption(choiceIndex, optionIndex)">{{$t('choice_option_remove')}}</button>
                                                                </div>
                                                            </div>
                                                        </template>
                                                    </div>
                                                </draggable>
                                            </div>
                                            <div v-else class="no-choice-options-box">
                                                <p>{{$t('no_choice_option_message')}}</p>
                                            </div>
                                        </div>
                                        <div v-if="['TEXT'].indexOf(choice.type) !== -1" class="form-row">
                                            <form-group class="form-group-sm col-4 col-sm-3 required" :label="$t('choice_required')" :label-for="`choices-${choiceIndex}-required`"
                                                        :invalid-feedback="validationErrors[`choices${choiceIndex}Required`]">
                                                <select :id="`choices-${choiceIndex}-required`" class="custom-select custom-select-sm" v-model="choice.required">
                                                    <option :value="false">{{$t("not_required")}}</option>
                                                    <option :value="true">{{$t("required")}}</option>
                                                </select>
                                            </form-group>
                                            <form-group class="form-group-sm col-8 col-sm-9" :label="$t('choice_note')" :label-for="`choices-${choiceIndex}-note`"
                                                        :invalid-feedback="validationErrors[`choices${choiceIndex}Note`]">
                                                <input :id="`choices-${choiceIndex}-note`" type="text" class="form-control form-control-sm" v-model="choice.note">
                                            </form-group>
                                        </div>
                                        <div class="form-row">
                                            <div class="col">
                                                <button type="button" class="btn btn-sm btn-outline-danger" @click="removeChoice(choiceIndex)">{{$t('choice_remove')}}</button>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </draggable>
                        </template>
                        <div v-else class="no-choices-box">
                            <p>{{$t('no_choice_message')}}</p>
                        </div>
                    </div>
                </div>
            </form-page>
            <footer>
                <div class="form-row">
                    <div class="col-auto">
                        <button type="submit" :disabled="product.saving" class="btn btn-primary"
                                v-text="!product.saving ? $t('save_button') : $t('saving')"></button>
                    </div>
                </div>
            </footer>
        </form>
      </article>
    </app-framework>
</template>

<style scoped>
    .form-group-sm {
        margin-bottom: 0.5rem
    }

    .choices-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;
    }

    .choices-header label {
        margin-bottom: 0;
    }
    .choices {
        padding: 5px 10px 10px;
        border-radius: 0.25rem;
        border: 1px solid #ced4da;
        background: #fff;
    }

    .choice + .choice {
        margin-top: 15px;
        border-top: 2px dashed #b4b9bd;
        padding-top: 10px;
    }

    .no-choices-box {
        text-align: center;
        padding: 10px;
    }

    .no-choices-box p {
        margin-bottom: 0;
    }

    .choice-options {
        margin: 0 -10px;
        padding: 0 10px;
    }

    .choices .choice-options-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;
    }

    .choices .choice-options-header label {
        font-size: .8rem;
    }

    .choice-options, .no-choice-options-box {
        margin-bottom: 1rem;
    }

    .choice-option {
        padding: 0 0 0.5rem 10px;
        border-left: 3px solid #ced4da;
    }

    .choice-option + .choice-option {
        margin-top: 1rem;
    }

    .no-choice-options-box {
        /*text-align: center;*/
        padding: 0.5rem 0 0.5rem 10px;
        border-left: 3px solid #ced4da;
    }

    .no-choice-options-box p {
        font-size: .8rem;
        margin-bottom: 0;
    }

    .reorder-choices-enabled.choices {
        padding: 10px;
    }

    .reorder-choices-enabled .choice + .choice {
        margin-top: 10px;
        border-top: none;
        padding-top: 0;
    }

    .reorder-choices-enabled .choice-reorder-title {
        font-size: 1rem;
        margin: 0;

        padding: 0.6rem;
        border-radius: 5px;
        border-style: dashed;
        border-width: 2px;
        border-color: #999;
        cursor: move;
    }

    .reorder-options-enabled.choice-options > div {
        padding: 0.5rem 0 0.5rem 10px;
        border-left: 3px solid #ced4da;
    }

    .reorder-options-enabled .choice-option {
        padding: 0;
        border-left: none;
    }

    .reorder-options-enabled .choice-option + .choice-option {
        margin-top: 10px;
    }

    .reorder-options-enabled .choice-option-reorder-description {
        font-size: 1rem;
        margin: 0;

        padding: 0.6rem;
        border-radius: 5px;
        border-style: dashed;
        border-width: 2px;
        border-color: #999;
        cursor: move;
    }
</style>
<style>
    .choices .form-label {
        font-size: .8rem;
    }
</style>

<script>
    import draggable from 'vuedraggable'
    import FormMixin from "@/mixins/FormMixin";
    import AppFramework from "@/components/layout/AppFramework";
    import FormPage from "@/components/common/FormPage";
    import FormGroup from "@/components/common/FormGroup";
    import LoadingState from "@/components/states/LoadingState";
    import InputImage from "@/components/common/InputImage";
    import gql from "graphql-tag";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";

    export default {
        name: 'productForm',
        i18nOptions: {namespaces: ['product_form', 'product', 'common', 'breadcrumbs', 'error']},
        components: {BreadCrumbs, InputImage, draggable, LoadingState, FormGroup, FormPage, AppFramework},
        mixins: [FormMixin],
        metaInfo() {
          return {
            title: this.$t(this.$route.params.id === 'new' ? 'product_form_new' : 'product_form_edit')
          }
        },
        data: () => ({
            product: {
                node: null,
                reorderChoicesEnabled: false,
                loading: true,
                error: null,
                saving: false
            },
            productCategories: {
                nodes: null,
                loading: true,
                error: null
            },
            productsSameCategory: {
                nodes: null,
                loading: true,
                error: null
            },
            productChoiceTypes: [
                "OPTIONS_SINGLE_CHOICE",
                "OPTIONS_MULTIPLE_CHOICE",
                "OPTIONS_REPEATABLE_CHOICE",
                "TEXT"
            ]
        }),
        computed: {
            isNew: function () {
                return this.$route.params.id === "new";
            },
        },
        mounted() {
            this.queryProduct();
            this.queryProductCategories();
            // await this.queryProductsSameCategory();
        },
        methods: {
            tryAgain() {
                if (!this.isNew && this.product.error) {
                    this.queryProduct();
                }
                if (this.productCategories.error) {
                    this.queryProductCategories();
                }
                // if (this.productsSameCategory.error) {
                //     this.queryProductsSameCategory();
                // }
            },
            async queryProduct() {
                if (this.isNew) {
                    this.product.node = {
                        productCategory: {},
                    };
                    this.product.loading = false
                    return;
                }

                this.product.loading = true
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($id: ID!) {
                            product(id: $id) {
                                id
                                name
                                productCategoryId
                                price
                                description
                                photoThumbUrl
                                photoUrl
                                choices {
                                    title
                                    type
                                    required
                                    note
                                    chargeOnlyMostExpensiveOption
                                    minOptions
                                    maxOptions
                                    options {
                                        description
                                        details
                                        price
                                    }
                                }
                                order
                                isDeleted
                            }
                        }`,
                        variables: {
                            id: this.$route.params.id
                        },
                        fetchPolicy: "network-only",
                    })
                    this.product.node = response.data.product
                    this.product.node.choices && this.product.node.choices.forEach(choice => {
                        this.$set(choice, "reorderOptionsEnabled", false)
                    })
                    this.product.error = null
                } catch (e) {
                    this.product.error = e.message
                }
                this.product.loading = false
            },
            async queryProductCategories() {
                this.productCategories.loading = true
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($menuId: ID!) {
                            productCategories (menuId: $menuId) {
                                id
                                name
                                order
                            }
                        }`,
                        variables: {
                          menuId: this.$route.params.menuId
                        }
                    })
                    this.productCategories.nodes = response.data.productCategories
                    this.productCategories.error = null
                } catch (e) {
                    this.productCategories.error = e.message
                }
                this.productCategories.loading = false;
            },
            // async queryProductsSameCategory(productCategoryId) {
            //     this.productsSameCategory.loading = true;
            //     try {
            //         const response = await this.$apolloClient.query({
            //             query: PRODUCTS,
            //             variables: {
            //                 productCategoryId: productCategoryId
            //             }
            //         });
            //         this.productsSameCategory.nodes = response.data.products;
            //     } catch (e) {
            //         this.productsSameCategory.error = e.message;
            //     }
            //     this.productsSameCategory.loading = false;
            // },
            async saveProduct() {
                if (this.product.saving || !this.checkForm()) {
                    return;
                }
                this.disableForm();
                this.product.saving = true;

                try {
                    await this.$refs.photoInput.waitForUpload()
                    if (this.isNew) {
                        await this.createProduct(this.product.node);
                    } else {
                        await this.updateProduct(this.product.node);
                    }
                    await this.$apolloClient.resetStore();
                    await this.$router.push(`/menus/${this.$route.params.menuId}/products`);
                } catch (e) {
                    this.product.saving = false;
                    this.activeForm();
                    this.showError(e);
                }
            },
            async createProduct(productData) {
                await this.$apolloClient.mutate({
                    mutation: gql`mutation ($input: ProductInput!) {
                        createProduct(
                            input: $input
                        ) {
                            id
                            name
                            productCategoryId
                            price
                            description
                            photoUrl
                            photoThumbUrl
                            choices {
                                title
                                type
                                required
                                note
                                chargeOnlyMostExpensiveOption
                                minOptions
                                maxOptions
                                options {
                                    description
                                    details
                                    price
                                }
                            }
                            order
                        }
                    }`,
                    variables: {
                        input: {
                            name: productData.name,
                            productCategoryId: productData.productCategoryId,
                            price: productData.price,
                            photoUrl: productData.photoUrl || null,
                            photoThumbUrl: productData.photoThumbUrl || null,
                            description: productData.description || null,
                            choices: productData.choices && productData.choices.map(choice => {
                              return {
                                type: choice.type,
                                title: choice.title,
                                required: choice.required,
                                chargeOnlyMostExpensiveOption: choice.chargeOnlyMostExpensiveOption,
                                minOptions: choice.minOptions,
                                maxOptions: choice.maxOptions,
                                note: choice.note,
                                options: choice.options && choice.options.map(option => {
                                  return {
                                    description: option.description,
                                    details: option.details || null,
                                    price: option.price || null,
                                  }
                                })
                              }
                            })
                        },
                    }
                });
            },
            async updateProduct(productData) {
                await this.$apolloClient.mutate({
                    mutation: gql`mutation ($id: ID! $input: ProductInput!) {
                        updateProduct(
                            id: $id
                            input: $input
                        ) {
                            id
                            name
                            productCategoryId
                            price
                            description
                            photoUrl
                            photoThumbUrl
                            choices {
                                title
                                type
                                required
                                note
                                chargeOnlyMostExpensiveOption
                                minOptions
                                maxOptions
                                options {
                                    description
                                    details
                                    price
                                }
                            }
                            order
                        }
                    }`,
                    variables: {
                        id: this.$route.params.id,
                        input: {
                            name: productData.name,
                            productCategoryId: productData.productCategoryId,
                            price: productData.price,
                            photoUrl: productData.photoUrl || null,
                            photoThumbUrl: productData.photoThumbUrl || null,
                            description: productData.description || null,
                            choices: productData.choices && productData.choices.map(choice => {
                                return {
                                    type: choice.type,
                                    title: choice.title,
                                    required: choice.required,
                                    chargeOnlyMostExpensiveOption: choice.chargeOnlyMostExpensiveOption,
                                    minOptions: choice.minOptions,
                                    maxOptions: choice.maxOptions,
                                    note: choice.note,
                                    options: choice.options && choice.options.map(option => {
                                        return {
                                            description: option.description,
                                            details: option.details || null,
                                            price: option.price || null,
                                        }
                                    })
                                }
                            })
                        },
                    }
                });
            },
            addChoice() {
                if (!this.product.node.choices) {
                    this.$set(this.product.node, "choices", [])
                }
                this.product.node.choices.push({
                    type: "OPTIONS_SINGLE_CHOICE",
                    reorderOptionsEnabled: false,
                    chargeOnlyMostExpensiveOption: false,
                    required: false
                })
            },
            removeChoice(choiceIndex) {
                if (!this.product.node.choices) {
                    return
                }
                this.product.node.choices.splice(choiceIndex, 1)
            },
            addChoiceOption(choice){
                if (!choice.options) {
                    this.$set(choice, "options", [])
                }
                choice.options.push({})
            },
            removeChoiceOption(choiceIndex, optionIndex) {
                if (!this.product.node.choices) {
                    return
                }
                if (!this.product.node.choices[choiceIndex].options) {
                    return
                }
                this.product.node.choices[choiceIndex].options.splice(optionIndex, 1)
            },
            validateForm() {
                this.validateFormMixin();
            }
        }
    }
</script>
